import {createAsyncThunk} from '@reduxjs/toolkit';
import {CLDispatch, RootState} from 'common/src/redux/store';
import {AssetFolderEntityType} from '../../../Assets.enums';
import {Folder} from '../../../Assets.types';
import {AssetFilterState} from '../../../utils/filterAssets';
import {refreshFolderUtil} from '../assetDisplayThunkUtils';

/**
 * Parameters for refreshCurrentFolder thunk
 */
export interface RefreshCurrentFolderParams {
  selectAssetTree: (state: RootState) => Folder | null;
  selectCurrentFolderId: (state: RootState) => number | null;
  setAssetTree: (tree: Folder | null) => {type: string; payload: Folder | null};
  setIsLoadingFolderAssets: (isLoading: boolean) => {type: string; payload: boolean};
  entityType: AssetFolderEntityType;
  treeFilters?: AssetFilterState;
}

/**
 * Unified thunk to refresh current folder contents
 * Used by all asset hooks with their slice-specific configuration
 */
export const refreshCurrentFolder = createAsyncThunk<
  void,
  RefreshCurrentFolderParams,
  {dispatch: CLDispatch; state: RootState}
>(
  'refreshCurrentFolder',
  async (
    {
      selectAssetTree,
      selectCurrentFolderId,
      setAssetTree,
      setIsLoadingFolderAssets,
      entityType,
      treeFilters,
    },
    {dispatch, getState, rejectWithValue},
  ) => {
    try {
      const state = getState();
      const assetTree = selectAssetTree(state);
      const currentFolderId = selectCurrentFolderId(state);

      if (!assetTree || currentFolderId === null) {
        throw new Error('Asset tree or current folder not loaded');
      }

      dispatch(setIsLoadingFolderAssets(true));

      // Refresh folder contents using shared utility
      const updatedTree = await refreshFolderUtil(
        assetTree,
        currentFolderId,
        entityType,
        0,
        treeFilters,
      );

      dispatch(setAssetTree(updatedTree));
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to refresh folder contents';

      return rejectWithValue(errorMessage);
    } finally {
      dispatch(setIsLoadingFolderAssets(false));
    }
  },
);

